import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Account.scss";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckIcon from "@mui/icons-material/Check";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { resetAuth, setUserData } from "../../reducers/authSlice";
import {
  authApi,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetCmsQuery,
  useGetFaqsQuery,
  useUploadMediaMutation,
  useChangePasswordMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
} from "../../services/auth";
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "libphonenumber-js";
import {
  DEFAULT_AVATAR,
  extractProfileData,

  getProfilePhotoUrl,
  mapProfileToForm,
  toProfilePicturePayload,
} from "../../utils/profile";
import {
  isDisallowedNameKey,
  sanitizeNameInput,
  validateNameField,
} from "../../utils/nameValidation";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  IconButton,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OTPInput from "react-otp-input";

const extractMediaPic = (
  mediaResponse: any
): string | number | null => {
  if (!mediaResponse) return null;
  const res = mediaResponse.data || mediaResponse;
  if (res.id != null) return res.id;
  if (res.media_id != null) return res.media_id;
  if (res.media_file_url) return res.media_file_url;
  if (res.url) return res.url;
  if (res.file_url) return res.file_url;
  if (res.image_url) return res.image_url;
  if (res.media) return res.media;





  return null;
};

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth.userData);

  const [logoutApi] = useLogoutMutation();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const getTabFromLocation = (pathname: string, stateTab?: string) => {
    if (pathname === "/manage-profile/security" || pathname === "/security") return "Security";
    if (pathname === "/manage-profile/support" || pathname === "/support") return "Support";
    if (pathname === "/manage-profile/faq" || pathname === "/faq") return "FAQ";
    if (pathname === "/privacy-policy" || pathname === "/pp" || pathname === "/manage-profile/privacy-policy") return "Privacy Policy";
    if (pathname === "/cms" || pathname === "/manage-profile/cms" || pathname === "/terms" || pathname === "/terms-and-conditions") return "Terms and Conditions";
    if (stateTab === "CMS") return "Terms and Conditions";
    return stateTab || "Profile";
  };

  const [activeTab, setActiveTab] = useState(() =>
    getTabFromLocation(location.pathname, location.state?.activeTab)
  );
  const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [phoneFormat, setPhoneFormat] = useState<string>("");

  const [originalContact, setOriginalContact] = useState({
    email: "",
    phone_number: "",
    country_code: "",
  });

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpTimeLeft, setOtpTimeLeft] = useState(60);
  const [otpResetKey, setOtpResetKey] = useState(0);

  const [verifyOtpApi, { isLoading: isVerifyingOtp }] =
    useVerifyOtpMutation();
  const [resendOtpApi, { isLoading: isResendingOtp }] =
    useResendOtpMutation();

  const [changePasswordApi, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();

  const [profileErrors, setProfileErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const togglePasswordVisibility = (
    field: "old" | "new" | "confirm"
  ) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (otpTimeLeft > 0 && isOtpModalOpen) {
      const timerId = setTimeout(
        () => setOtpTimeLeft(otpTimeLeft - 1),
        1000
      );

      return () => clearTimeout(timerId);
    }
  }, [otpTimeLeft, isOtpModalOpen]);

  const {
    data: profileResponse,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useGetProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [updateProfileApi, { isLoading: isUpdatingProfile }] =
    useUpdateProfileMutation();

  const [uploadMediaApi, { isLoading: isUploadingMedia }] =
    useUploadMediaMutation();

  const { data: cmsData, isLoading: isCmsLoading } = useGetCmsQuery(
    undefined,
    {
      skip:
        activeTab !== "Terms and Conditions" &&
        activeTab !== "Privacy Policy",
    }
  );

  const { data: faqsData, isLoading: isFaqsLoading } = useGetFaqsQuery(
    debouncedSearchQuery ? { search: debouncedSearchQuery } : undefined,
    { skip: activeTab !== "FAQ" }
  );

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    country_code: "",
    email: "",
    profile_picture: null as any,
  });

  useEffect(() => {
    const mapped = mapProfileToForm(profileResponse);
    if (!mapped) return;

    setOriginalContact({
      email: mapped.email,
      phone_number: mapped.phone_number,
      country_code: mapped.country_code,
    });

    setFormData((prev) => {
      if (selectedImageFile) {
        return { ...mapped, profile_picture: prev.profile_picture };
      }
      if (!getProfilePhotoUrl(mapped.profile_picture) && getProfilePhotoUrl(prev.profile_picture)) {
        return { ...mapped, profile_picture: prev.profile_picture };
      }

      return mapped;
    });

    if (!selectedImageFile) setPreviewUrl(null);

    const profile = extractProfileData(profileResponse);
    if (profile) dispatch(setUserData(profile));
  }, [profileResponse, dispatch, selectedImageFile]);

  useEffect(() => {
    if (!profileError) return;

    const err: any = profileError;

    toast.error(
      err?.data?.message ||
      err?.error ||
      "Failed to load profile details"
    );
  }, [profileError]);

  const validateEmailField = (value: string) => {
    if (!value) return "Email is required";
    if (value.length > 80)
      return "Email must not exceed 80 characters";

    const emailRegex =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(value))
      return "Please enter a valid email address";

    return "";
  };

  const validatePhoneField = (
    value: string,
    countryCode?: string
  ) => {
    if (!value) return "Phone number is required";

    const cc = countryCode || formData.country_code || "+91";
    const cleanCc = cc.startsWith("+") ? cc : `+${cc}`;
    const cleanValue = value.replace(/[^\d+]/g, "");

    const fullNumber = cleanValue.startsWith("+")
      ? cleanValue
      : `${cleanCc}${cleanValue.replace(/^\+/, "")}`;

    if (!isValidPhoneNumber(fullNumber)) {
      return "Please enter a valid phone number for the selected country";
    }

    return "";
  };

  const validateProfileField = (
    name: string,
    value: string,
    countryCode?: string
  ) => {
    switch (name) {
      case "first_name":
      case "last_name": {
        const fieldName =
          name === "first_name" ? "First Name" : "Last Name";

        return validateNameField(fieldName, value) || "";
      }

      case "email":
        return validateEmailField(value);

      case "phone_number":
        return validatePhoneField(value, countryCode);

      default:
        return "";
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    const nextValue =
      name === "first_name" || name === "last_name"
        ? sanitizeNameInput(value)
        : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    if (name === "first_name" || name === "last_name") {
      const fieldName =
        name === "first_name" ? "First Name" : "Last Name";

      const error =
        value !== nextValue
          ? `${fieldName} can only contain letters`
          : validateProfileField(name, nextValue);

      setProfileErrors((prev) => ({
        ...prev,
        [name]: error,
      }));

      return;
    }

    const error = validateProfileField(name, nextValue);

    setProfileErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleNameKeyDown =
    (field: "first_name" | "last_name") =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.nativeEvent.isComposing) return;
        if (!isDisallowedNameKey(e.key)) return;

        e.preventDefault();

        const fieldName =
          field === "first_name" ? "First Name" : "Last Name";

        setProfileErrors((prev) => ({
          ...prev,
          [field]: `${fieldName} can only contain letters`,
        }));
      };

  const handleProfileSubmit = async () => {
    const trimmedFirstName = sanitizeNameInput(
      formData.first_name
    ).trim();

    const trimmedLastName = sanitizeNameInput(
      formData.last_name
    ).trim();

    const errFirst = validateProfileField(
      "first_name",
      trimmedFirstName
    );

    const errLast = validateProfileField(
      "last_name",
      trimmedLastName
    );

    const errEmail = validateProfileField(
      "email",
      formData.email
    );

    const errPhone = validateProfileField(
      "phone_number",
      formData.phone_number,
      formData.country_code
    );

    setProfileErrors({
      first_name: errFirst,
      last_name: errLast,
      email: errEmail,
      phone_number: errPhone,
    });

    if (errFirst || errLast || errEmail || errPhone) {
      return;
    }

    await executeProfileUpdate(
      trimmedFirstName,
      trimmedLastName
    );
  };

  const triggerOtpFlow = async () => {
    const emailChanged =
      formData.email !== originalContact.email;

    const phoneChanged =
      formData.phone_number !== originalContact.phone_number ||
      formData.country_code !== originalContact.country_code;

    try {
      const payload: any = {};

      if (emailChanged) payload.email = formData.email;

      if (phoneChanged) {
        payload.phone_number = formData.phone_number;

        if (formData.country_code) {
          payload.country_code = formData.country_code;
        }
      }

      await resendOtpApi(payload).unwrap();

      setOtpTimeLeft(60);
      setOtp("");
      setOtpResetKey((prev) => prev + 1);
      setIsOtpModalOpen(true);

      toast.success("OTP sent for verification.");
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
        err?.message ||
        "Failed to send OTP."
      );
    }
  };

  const handleVerifyOtpAndSave = async () => {
    if (otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    const emailChanged =
      formData.email !== originalContact.email;

    const phoneChanged =
      formData.phone_number !== originalContact.phone_number ||
      formData.country_code !== originalContact.country_code;

    try {
      const payload: any = { otp };

      if (emailChanged) payload.email = formData.email;

      if (phoneChanged) {
        payload.phone_number = formData.phone_number;

        if (formData.country_code) {
          payload.country_code = formData.country_code;
        }
      }

      await verifyOtpApi(payload).unwrap();

      setIsOtpModalOpen(false);

      const trimmedFirstName = formData.first_name.trim();
      const trimmedLastName = formData.last_name.trim();

      await executeProfileUpdate(
        trimmedFirstName,
        trimmedLastName
      );
    } catch (err: any) {
      const errorMsg =
        err?.data?.message ||
        err?.message ||
        "Failed to verify OTP";

      toast.error(errorMsg);
    }
  };

  const executeProfileUpdate = async (
    trimmedFirstName: string,
    trimmedLastName: string
  ) => {
    try {
      const payload: any = {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        email: formData.email,
        phone_number: formData.phone_number,
      };

      if (formData.country_code) {
        payload.country_code = formData.country_code;
      }

      const pictureId = toProfilePicturePayload(
        formData.profile_picture
      );

      if (pictureId != null) {
        payload.profile_picture = pictureId;
      }

      const res = await updateProfileApi(payload).unwrap();

      const extracted = extractProfileData(res);
      const currentPhotoObj = formData.profile_picture;
      const currentPhotoUrl = getProfilePhotoUrl(currentPhotoObj);
      const resPhotoUrl = getProfilePhotoUrl(extracted?.profile_picture);

      const updatedProfile: Record<string, any> = extracted
        ? {
          ...extracted,
          ...(!resPhotoUrl && currentPhotoUrl ? { profile_picture: currentPhotoObj } : {}),
        }
        : currentPhotoUrl
          ? { profile_picture: currentPhotoObj }
          : {};
      if (updatedProfile) dispatch(setUserData(updatedProfile));

      const mapped = mapProfileToForm(res);

      if (mapped) {
        const nextFormData = {
          ...mapped,
          ...(!getProfilePhotoUrl(mapped.profile_picture) && currentPhotoUrl
            ? { profile_picture: currentPhotoObj }
            : {}),
        };
        setFormData(nextFormData);

        setOriginalContact({



          email: nextFormData.email,
          phone_number: nextFormData.phone_number,
          country_code: nextFormData.country_code,
        });
      }

      else if (currentPhotoUrl) {
        setFormData((prev) => ({
          ...prev,
          profile_picture: currentPhotoObj,
        }));
      }

      setSelectedImageFile(null);
      setPreviewUrl(null);
      try {
        refetchProfile();
      } catch {}
  


    toast.success(
      res?.message || "Profile updated successfully!"
    );
  } catch (err: any) {
    toast.error(
      err?.data?.message ||
      err?.message ||
      "Failed to update profile"
    );
  }
};

const validateNewPassword = (
  value: string,
  oldPassword?: string
) => {
  if (!value) return "New Password is required";
  if (value.includes(" "))
    return "Password must not contain spaces";

  if (value.length < 8 || value.length > 30) {
    return "Password must be between 8 and 30 characters";
  }

  if (!/[A-Z]/.test(value))
    return "Password must contain at least one uppercase letter";

  if (!/[a-z]/.test(value))
    return "Password must contain at least one lowercase letter";

  if (!/[0-9]/.test(value))
    return "Password must contain at least one number";

  if (!/[^A-Za-z0-9]/.test(value)) {
    return "Password must contain at least one special character";
  }

  if (oldPassword && value === oldPassword) {
    return "New Password should not be the same as Current Password";
  }

  return "";
};

const validateConfirmPassword = (
  value: string,
  newPassword?: string
) => {
  if (!value) return "Confirm Password is required";

  if (newPassword && value !== newPassword) {
    return "Confirm Password must exactly match New Password";
  }

  return "";
};

const validatePasswordField = (
  name: string,
  value: string,
  passwordsState: any
) => {
  switch (name) {
    case "old_password":
      return !value ? "Current Password is required" : "";

    case "new_password":
      return validateNewPassword(
        value,
        passwordsState.old_password
      );

    case "confirm_password":
      return validateConfirmPassword(
        value,
        passwordsState.new_password
      );

    default:
      return "";
  }
};

const handlePasswordChange = (
  e: React.ChangeEvent<HTMLInputElement>
) => {
  const { name, value } = e.target;

  const newPasswordData = {
    ...passwordData,
    [name]: value,
  };

  setPasswordData(newPasswordData);

  const error = validatePasswordField(
    name,
    value,
    newPasswordData
  );

  setPasswordErrors((prev) => ({
    ...prev,
    [name]: error,
  }));

  if (name === "old_password" && passwordErrors.new_password) {
    const newPassError = validatePasswordField(
      "new_password",
      newPasswordData.new_password,
      newPasswordData
    );

    setPasswordErrors((prev) => ({
      ...prev,
      new_password: newPassError,
    }));
  }

  if (
    name === "new_password" &&
    passwordErrors.confirm_password
  ) {
    const confirmPassError = validatePasswordField(
      "confirm_password",
      newPasswordData.confirm_password,
      newPasswordData
    );

    setPasswordErrors((prev) => ({
      ...prev,
      confirm_password: confirmPassError,
    }));
  }
};

const handlePasswordSubmit = async () => {
  const errOld = validatePasswordField(
    "old_password",
    passwordData.old_password,
    passwordData
  );

  const errNew = validatePasswordField(
    "new_password",
    passwordData.new_password,
    passwordData
  );

  const errConfirm = validatePasswordField(
    "confirm_password",
    passwordData.confirm_password,
    passwordData
  );

  setPasswordErrors({
    old_password: errOld,
    new_password: errNew,
    confirm_password: errConfirm,
  });

  if (errOld || errNew || errConfirm) {
    return;
  }

  try {
    await changePasswordApi({
      old_password: passwordData.old_password,
      new_password: passwordData.new_password,
      password: passwordData.new_password,
      confirm_password: passwordData.confirm_password,
    }).unwrap();

    toast.success("Password changed successfully!");

    setPasswordData({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });

    setPasswordErrors({
      old_password: "",
      new_password: "",
      confirm_password: "",
    });
  } catch (err: any) {
    let errorMsg =
      err?.data?.message ||
      err?.message ||
      "Failed to change password";

    if (typeof errorMsg === "string") {
      errorMsg = errorMsg.replace(/_/g, " ");
    }

    toast.error(errorMsg);
  }
};

useEffect(() => {
  const nextTab = getTabFromLocation(location.pathname, location.state?.activeTab);
  setActiveTab(nextTab);
}, [location.pathname, location.state?.activeTab]);

const handleTabChange = (tab: string) => {
  if (tab === "Profile") {
    navigate("/manage-profile");
  } else if (tab === "Security") {
    navigate("/security");
  } else if (tab === "Support") {
    navigate("/support");
  } else if (tab === "Terms and Conditions") {
    navigate("/cms");
  } else if (tab === "Privacy Policy") {
    navigate("/privacy-policy");
  } else if (tab === "FAQ") {
    navigate("/faq");
  } else {
    navigate("/manage-profile", { state: { activeTab: tab } });
  }
};

const handleLogoutClick = () => {
  setIsLogoutDialogOpen(true);
};

const confirmLogout = async () => {
  if (isLoggingOut) return;

  setIsLoggingOut(true);

  try {
    await logoutApi({}).unwrap();
  } catch (error: any) {
    console.error("Logout API error:", error);
  } finally {
    dispatch(resetAuth());
    dispatch(authApi.util.resetApiState());
    setIsLogoutDialogOpen(false);
    setIsLoggingOut(false);

    toast.success("Logged out successfully");

    navigate("/", { replace: true });
  }
};

const cancelLogout = () => {
  if (isLoggingOut) return;
  setIsLogoutDialogOpen(false);
};

const getHeaderTitle = () => {
  if (activeTab === "Security") return "Change Password";
  if (activeTab === "FAQ") return "Frequently Asked Questions";
  return activeTab;
};

const renderSidebar = () => (
  <aside className="account_sidebar">
    <div className="account_sidebar__header">
      <h2>My Account</h2>
      <p>
        Manage your profile, security and get help from support
      </p>
    </div>

    <ul className="account_sidebar__menu">
      <li
        className={activeTab === "Profile" ? "active" : ""}
        onClick={() => handleTabChange("Profile")}
      >
        <div className="icon_wrapper">
          <PersonOutlineIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Profile</span>
          <span className="subtitle">
            View and edit your personal info
          </span>
        </div>
      </li>

      <li
        className={activeTab === "Security" ? "active" : ""}
        onClick={() => handleTabChange("Security")}
      >
        <div className="icon_wrapper">
          <SecurityIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Security</span>
          <span className="subtitle">
            Change your password.
          </span>
        </div>
      </li>

      <li
        className={activeTab === "Support" ? "active" : ""}
        onClick={() => handleTabChange("Support")}
      >
        <div className="icon_wrapper">
          <SupportAgentIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Support</span>
          <span className="subtitle">
            Get help and learn more
          </span>
        </div>
      </li>

      <li
        className={
          activeTab === "Terms and Conditions" ? "active" : ""
        }
        onClick={() => handleTabChange("Terms and Conditions")}
      >
        <div className="icon_wrapper">
          <DescriptionOutlinedIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Terms and Conditions</span>
          <span className="subtitle">
            company Terms and Conditions
          </span>
        </div>
      </li>

      <li
        className={activeTab === "Privacy Policy" ? "active" : ""}
        onClick={() => handleTabChange("Privacy Policy")}
      >
        <div className="icon_wrapper">
          <PrivacyTipOutlinedIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Privacy Policy</span>
          <span className="subtitle">
            Company Privacy Policy
          </span>
        </div>
      </li>

      <li
        className={activeTab === "FAQ" ? "active" : ""}
        onClick={() => handleTabChange("FAQ")}
      >
        <div className="icon_wrapper">
          <HelpOutlineIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">FAQ</span>
          <span className="subtitle">
            Frequently Asked Questions.
          </span>
        </div>
      </li>

      <li
        className={activeTab === "Logout" ? "active" : ""}
        onClick={handleLogoutClick}
      >
        <div className="icon_wrapper">
          <LogoutIcon />
        </div>
        <div className="text_wrapper">
          <span className="title">Logout</span>
          <span className="subtitle">
            You will be logout from platform
          </span>
        </div>
      </li>
    </ul>
  </aside>
);

const renderProfileImageSection = () => (
  <>
    <div className="profile_image_section">
      <div className="image_wrapper">
        <img
          src={
            previewUrl ||
            getProfilePhotoUrl(formData.profile_picture) ||
            getProfilePhotoUrl(authUser) ||
            DEFAULT_AVATAR
          }
          alt="Profile"
          style={{ objectFit: "cover" }}
          onError={(e) => {
            const current = e.currentTarget.src;
            if (!current.includes("dummy-user.webp") && !current.includes("avtar.svg")) {
              e.currentTarget.src = DEFAULT_AVATAR;
            } else if (!current.includes("avtar.svg")) {
              e.currentTarget.src = "/static/images/avtar.svg";
            }
          }}
        />

        <div
          className="camera_badge"
          onClick={() =>
            document
              .getElementById("profile-image-upload")
              ?.click()
          }
          style={{ cursor: "pointer" }}
        >
          <CameraAltIcon />
        </div>
      </div>

      <div className="image_info">
        JPG, PNG or GIF.
        <br />
        Max size 2MB.
      </div>

      <input
        type="file"
        accept="image/png, image/jpeg, image/gif"
        style={{ display: "none" }}
        id="profile-image-upload"
        onChange={async (e) => {
          const file = e.target.files?.[0];

          if (file) {
            const validTypes = [
              "image/jpeg",
              "image/png",
              "image/gif",
            ];

            if (!validTypes.includes(file.type)) {
              toast.error(
                "Please upload an image in a supported format: JPG, JPEG, PNG, or GIF."
              );

              e.target.value = "";
              return;
            }

            if (file.size > 2 * 1024 * 1024) {
              toast.error(
                "Image size must not exceed 2 MB."
              );

              e.target.value = "";
              return;
            }

            setSelectedImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));

            const mediaData = new FormData();
            mediaData.append("media_file", file);
            mediaData.append("file_type", "img");

            try {
              const mediaResponse =
                await uploadMediaApi(mediaData).unwrap();

              const newPic = extractMediaPic(mediaResponse);

              if (newPic != null) {
                const media =
                  mediaResponse?.data || mediaResponse;

                setFormData((prev) => ({
                  ...prev,

                  profile_picture: typeof media === "object" && media !== null ? media : newPic,


                }));

                toast.success(
                  "Profile photo uploaded successfully!"
                );
              }
            } catch (err: any) {
              toast.error(
                "Failed to upload image. Please try again."
              );
            }
          }
        }}
      />

      <button
        className="btn_change_photo"
        onClick={() =>
          document
            .getElementById("profile-image-upload")
            ?.click()
        }
        disabled={isUploadingMedia || isUpdatingProfile}
      >
        <FileUploadOutlinedIcon />
        {isUploadingMedia ? "Uploading..." : "Change Photo"}
      </button>
    </div>
  </>
);

const renderProfileFormSection = () => (
  <>
    <div className="profile_form_section">
      <div className="form_row">
        <div className="form_group">
          <label>First Name</label>

          <div
            className="input_wrapper"
            style={{
              border: profileErrors.first_name
                ? "1px solid #d32f2f"
                : "1px solid transparent",
              borderRadius: "12px",
            }}
          >
            <span className="input_icon">
              <PersonOutlineIcon />
            </span>

            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              onKeyDown={handleNameKeyDown("first_name")}
              placeholder="Enter first name"
              autoComplete="given-name"
              inputMode="text"
              maxLength={35}
              disabled={isUpdatingProfile}
            />
          </div>

          {profileErrors.first_name && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "12px",
                marginTop: "5px",
                marginBottom: 0,
              }}
            >
              {profileErrors.first_name}
            </p>
          )}
        </div>

        <div className="form_group">
          <label>Last Name</label>

          <div
            className="input_wrapper"
            style={{
              border: profileErrors.last_name
                ? "1px solid #d32f2f"
                : "1px solid transparent",
              borderRadius: "12px",
            }}
          >
            <span className="input_icon">
              <PersonOutlineIcon />
            </span>

            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              onKeyDown={handleNameKeyDown("last_name")}
              placeholder="Enter last name"
              autoComplete="family-name"
              inputMode="text"
              maxLength={35}
              disabled={isUpdatingProfile}
            />
          </div>

          {profileErrors.last_name && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "12px",
                marginTop: "5px",
                marginBottom: 0,
              }}
            >
              {profileErrors.last_name}
            </p>
          )}
        </div>
      </div>

      <div className="form_group">
        <label>Phone Number</label>

        <div style={{ position: "relative" }}>
          <PhoneInput
            country="ae"
            value={
              formData.phone_number
                ? `${(
                  formData.country_code || "+971"
                ).replace(/\D/g, "")}${formData.phone_number.replace(
                  /\D/g,
                  ""
                )}`
                : ""
            }
            countryCodeEditable={false}
            onChange={(val, data: any) => {
              const dialCode = data?.dialCode || "971";
              const selectedCc = `+${dialCode}`;
              const digits = val.replace(/\D/g, "");

              const phoneNumber =
                digits.length <= dialCode.length
                  ? ""
                  : digits.startsWith(dialCode)
                    ? digits.slice(dialCode.length)
                    : digits;

              setFormData((prev) => ({
                ...prev,
                country_code: selectedCc,
                phone_number: phoneNumber,
              }));

              if (data?.format) {
                setPhoneFormat(data.format);
              }

              const errorMsg = validatePhoneField(
                phoneNumber,
                selectedCc
              );

              setProfileErrors((prev) => ({
                ...prev,
                phone_number: errorMsg,
              }));
            }}
            placeholder="Enter your phone number"
            enableSearch
            disabled={isUpdatingProfile}
            inputStyle={{
              width: "100%",
              height: "50px",
              background: "rgba(255, 255, 255, 0.05)",
              border: profileErrors.phone_number
                ? "1px solid #d32f2f"
                : "1px solid transparent",
              borderRadius: "12px",
              color: "#fff",
            }}
            buttonStyle={{
              background: "transparent",
              border: "none",
              borderRadius: "12px 0 0 12px",
            }}
            dropdownStyle={{
              background: "#1e1e1e",
              color: "#fff",
            }}
          />
        </div>

        {profileErrors.phone_number && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "5px",
              marginBottom: 0,
            }}
          >
            {profileErrors.phone_number}
          </p>
        )}
      </div>

      <div className="form_group">
        <label>Email Address</label>

        <div
          className="input_wrapper"
          style={{
            border: profileErrors.email
              ? "1px solid #d32f2f"
              : "1px solid transparent",
            borderRadius: "12px",
          }}
        >
          <span className="input_icon">
            <MailOutlineIcon />
          </span>

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
            disabled={isUpdatingProfile}
          />
        </div>

        {profileErrors.email && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "5px",
              marginBottom: 0,
            }}
          >
            {profileErrors.email}
          </p>
        )}
      </div>

      <div className="form_actions">
        <button
          className="btn_save"
          onClick={handleProfileSubmit}
          disabled={isUpdatingProfile || isUploadingMedia}
        >
          <CheckIcon />
          {isUpdatingProfile || isUploadingMedia
            ? "Saving..."
            : "Save Changes"}
        </button>
      </div>
    </div>
  </>
);

const renderProfileTab = () => (
  <div className="account_content__body">
    {isProfileLoading && !profileResponse ? (
      <div
        style={{
          textAlign: "center",
          padding: "2rem",
          width: "100%",
        }}
      >
        Loading profile data...
      </div>
    ) : profileError && !profileResponse ? (
      <div
        style={{
          textAlign: "center",
          padding: "2rem",
          width: "100%",
        }}
      >
        <p
          style={{
            color: "rgba(255,255,255,0.7)",
            marginBottom: 16,
          }}
        >
          Could not load profile details.
        </p>

        <button
          className="btn_save"
          type="button"
          onClick={() => refetchProfile()}
        >
          Try again
        </button>
      </div>
    ) : (
      <>
        {renderProfileImageSection()}
        {renderProfileFormSection()}
      </>
    )}
  </div>
);

const renderSecurityTab = () => (
  <div className="account_content__body">
    <div className="profile_form_section">
      <div className="form_group">
        <label>Current Password</label>

        <div
          className="input_wrapper"
          style={{
            border: passwordErrors.old_password
              ? "1px solid #d32f2f"
              : "1px solid transparent",
            borderRadius: "12px",
          }}
        >
          <span className="input_icon">
            <LockOutlinedIcon />
          </span>

          <input
            type={showPasswords.old ? "text" : "password"}
            name="old_password"
            placeholder="Enter your current password"
            value={passwordData.old_password}
            onChange={handlePasswordChange}
            disabled={isChangingPassword}
            style={{ paddingRight: "45px" }}
          />

          <IconButton
            onClick={() => togglePasswordVisibility("old")}
            style={{
              position: "absolute",
              right: "10px",
              color: "rgba(255, 255, 255, 0.4)",
            }}
          >
            {showPasswords.old ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>

        {passwordErrors.old_password && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "5px",
              marginBottom: 0,
            }}
          >
            {passwordErrors.old_password}
          </p>
        )}
      </div>

      <div className="form_group">
        <label>New Password</label>

        <div
          className="input_wrapper"
          style={{
            border: passwordErrors.new_password
              ? "1px solid #d32f2f"
              : "1px solid transparent",
            borderRadius: "12px",
          }}
        >
          <span className="input_icon">
            <LockOutlinedIcon />
          </span>

          <input
            type={showPasswords.new ? "text" : "password"}
            name="new_password"
            placeholder="Enter new password"
            value={passwordData.new_password}
            onChange={handlePasswordChange}
            disabled={isChangingPassword}
            style={{ paddingRight: "45px" }}
          />

          <IconButton
            onClick={() => togglePasswordVisibility("new")}
            style={{
              position: "absolute",
              right: "10px",
              color: "rgba(255, 255, 255, 0.4)",
            }}
          >
            {showPasswords.new ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>

        {passwordErrors.new_password && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "5px",
              marginBottom: 0,
            }}
          >
            {passwordErrors.new_password}
          </p>
        )}
      </div>

      <div className="form_group">
        <label>Confirm Password</label>

        <div
          className="input_wrapper"
          style={{
            border: passwordErrors.confirm_password
              ? "1px solid #d32f2f"
              : "1px solid transparent",
            borderRadius: "12px",
          }}
        >
          <span className="input_icon">
            <LockOutlinedIcon />
          </span>

          <input
            type={showPasswords.confirm ? "text" : "password"}
            name="confirm_password"
            placeholder="Confirm new password"
            value={passwordData.confirm_password}
            onChange={handlePasswordChange}
            disabled={isChangingPassword}
            style={{ paddingRight: "45px" }}
          />

          <IconButton
            onClick={() => togglePasswordVisibility("confirm")}
            style={{
              position: "absolute",
              right: "10px",
              color: "rgba(255, 255, 255, 0.4)",
            }}
          >
            {showPasswords.confirm ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </div>

        {passwordErrors.confirm_password && (
          <p
            style={{
              color: "#d32f2f",
              fontSize: "12px",
              marginTop: "5px",
              marginBottom: 0,
            }}
          >
            {passwordErrors.confirm_password}
          </p>
        )}
      </div>

      <div className="form_actions">
        <button
          className="btn_save"
          onClick={handlePasswordSubmit}
          disabled={isChangingPassword}
        >
          <CheckIcon />
          {isChangingPassword ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  </div>
);

return (
  <div className="account_page">
    {renderSidebar()}

    <section className="account_content">
      <div className="account_content__header">
        <h2>{getHeaderTitle()}</h2>
      </div>

      {activeTab === "Profile" && renderProfileTab()}
      {activeTab === "Security" && renderSecurityTab()}

      {activeTab === "Support" && (
        <div
          className="account_content__body"
          style={{ background: "transparent", padding: 0 }}
        >
          <div className="support_cards_section">
            <div className="support_card">
              <div className="support_card__header">
                <div className="icon_wrapper">
                  <LocalPhoneOutlinedIcon />
                </div>

                <div className="text_wrapper">
                  <h3>Phone Number</h3>
                  <p>
                    Call us directly for immediate assistance.
                  </p>
                </div>
              </div>

              <div className="support_card__content">
                +1 (415) 555-0123
              </div>

              <button className="btn_outline">
                <LocalPhoneOutlinedIcon />
                Call Now
              </button>
            </div>

            <div className="support_card">
              <div className="support_card__header">
                <div className="icon_wrapper">
                  <MailOutlineIcon />
                </div>

                <div className="text_wrapper">
                  <h3>Email Address</h3>
                  <p>
                    Send us an email and we'll respond within 24 hours.
                  </p>
                </div>
              </div>

              <div className="support_card__content">
                support@itsmywall.com
              </div>

              <button className="btn_outline">
                <MailOutlineIcon />
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "Terms and Conditions" && (
        <div
          className="account_content__body"
          style={{ background: "transparent", padding: 0 }}
        >
          <div className="text_document_section">
            {isCmsLoading ? (
              <p>Loading Terms and Conditions...</p>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    cmsData?.data?.terms_and_conditions ||
                    "<p>No terms and conditions available.</p>",
                }}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === "Privacy Policy" && (
        <div
          className="account_content__body"
          style={{ background: "transparent", padding: 0 }}
        >
          <div className="text_document_section">
            {isCmsLoading ? (
              <p>Loading Privacy Policy...</p>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    cmsData?.data?.privacy_policy ||
                    "<p>No privacy policy available.</p>",
                }}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === "FAQ" && (
        <div
          className="account_content__body"
          style={{
            background: "transparent",
            padding: 0,
            flexDirection: "column",
            gap: "25px",
          }}
        >
          <div className="faq_section">
            <div className="faq_search">
              <div className="search_icon">
                <SearchIcon />
              </div>

              <input
                type="text"
                placeholder="Search for Question here......."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="faq_list">
              {isFaqsLoading ? (
                <p style={{ padding: "20px" }}>
                  Loading FAQs...
                </p>
              ) : (
                (faqsData?.data || []).map(
                  (faq: any, index: number) => {
                    const isExpanded = expandedFaq === index;

                    return (
                      <div className="faq_item" key={index}>
                        <div
                          className={`faq_question ${isExpanded ? "open" : ""
                            }`}
                          onClick={() =>
                            setExpandedFaq(
                              isExpanded ? null : index
                            )
                          }
                        >
                          {faq.question}

                          <span
                            className="toggle_icon"
                            style={{ pointerEvents: "none" }}
                          >
                            {isExpanded ? (
                              <RemoveIcon />
                            ) : (
                              <AddIcon />
                            )}
                          </span>
                        </div>

                        <div
                          className={`faq_answer ${isExpanded ? "open" : ""
                            }`}
                          dangerouslySetInnerHTML={{
                            __html: faq.answer,
                          }}
                        />
                      </div>
                    );
                  }
                )
              )}
            </div>
          </div>
        </div>
      )}
    </section>

    <Dialog
      open={isLogoutDialogOpen}
      onClose={cancelLogout}
      aria-labelledby="logout-dialog-title"
      aria-describedby="logout-dialog-description"
      className="logout_dialog"
      PaperProps={{
        className: "logout_dialog__paper",
      }}
    >
      <DialogContent className="logout_dialog__content">
        <div className="logout_dialog__icon">
          <LogoutIcon />
        </div>

        <h2
          id="logout-dialog-title"
          className="logout_dialog__title"
        >
          Log out of your account?
        </h2>

        <p
          id="logout-dialog-description"
          className="logout_dialog__text"
        >
          You'll need to sign in again to access your designs and saved work.
        </p>

        <div className="logout_dialog__actions">
          <Button
            className="logout_dialog__cancel"
            onClick={cancelLogout}
            disabled={isLoggingOut}
          >
            Cancel
          </Button>

          <Button
            className="logout_dialog__confirm"
            onClick={confirmLogout}
            disabled={isLoggingOut}
            autoFocus
          >
            {isLoggingOut ? (
              <>
                <CircularProgress size={16} color="inherit" />
                Logging out...
              </>
            ) : (
              "Log out"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog
      open={isOtpModalOpen}
      onClose={() =>
        !isVerifyingOtp && setIsOtpModalOpen(false)
      }
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          background: "#1e1e1e",
          color: "#fff",
          borderRadius: "16px",
        },
      }}
    >
      <div style={{ padding: "40px" }} className="authBox otpBox">
        <h2 style={{ marginBottom: "15px" }}>
          Verify Contact Information
        </h2>

        <p
          className="subtitle"
          style={{
            marginBottom: 40,
            color: "rgba(255, 255, 255, 0.7)",
          }}
        >
          We have sent a verification OTP to your new contact information.
          <br />
          Kindly check to retrieve the code.
        </p>

        <div className="control_group">
          <div
            className="opt_fields"
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <OTPInput
              key={otpResetKey}
              value={otp}
              onChange={setOtp}
              numInputs={4}
              renderInput={(props) => (
                <input
                  {...props}
                  style={{
                    ...props.style,
                    width: "50px",
                    height: "50px",
                    fontSize: "20px",
                    textAlign: "center",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(255, 255, 255, 0.05)",
                    color: "#fff",
                  }}
                />
              )}
              inputType="tel"
              shouldAutoFocus
              containerStyle={{
                gap: "20px",
                justifyContent: "center",
              }}
            />
          </div>

          <div
            className="otp_footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            <span className="timer">
              {Math.floor(otpTimeLeft / 60)
                .toString()
                .padStart(2, "0")}
              :
              {(otpTimeLeft % 60)
                .toString()
                .padStart(2, "0")}
            </span>

            <button
              type="button"
              className="resend_link"
              disabled={otpTimeLeft > 0 || isResendingOtp}
              onClick={triggerOtpFlow}
              style={{
                cursor:
                  otpTimeLeft > 0 || isResendingOtp
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  otpTimeLeft > 0 || isResendingOtp ? 0.5 : 1,
                background: "none",
                border: "none",
                color: "#8CE352",
                fontWeight: 600,
              }}
            >
              {isResendingOtp ? "Resending..." : "Resend"}
            </button>
          </div>
        </div>

        <div
          className="form_btn"
          style={{
            marginTop: "30px",
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            className="btn btn_outline"
            onClick={() => setIsOtpModalOpen(false)}
            disabled={isVerifyingOtp}
            style={{
              flex: 1,
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",
              textTransform: "none",
              padding: "12px",
            }}
          >
            Cancel
          </Button>

          <Button
            className="btn btn_primary"
            onClick={handleVerifyOtpAndSave}
            disabled={isVerifyingOtp}
            style={{
              flex: 1,
              background: "#8CE352",
              color: "#000",
              fontWeight: 600,
              textTransform: "none",
              padding: "12px",
            }}
          >
            {isVerifyingOtp ? "Verifying..." : "Verify & Save"}
          </Button>
        </div>
      </div>
    </Dialog>
  </div>
);
};

export default Account;